import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { usePermissions } from "components/Hook";
import PageLoader from "components/common/PageLoader";
import TimesheetNotActivated from "./TimesheetNotActivated";
import CustomerTimesheetView from "./CustomerTimesheetView";
import { updateClockinSettings, getClockinHistory } from "api/clockInWorkforce";
import { showSnackbar } from "store/actions/Utility";
import { GetEmployeeDetails } from "store/actions/GetEmployeeDetails";
import "./TimesheetTab.scss";

function TimesheetTab() {
  const dispatch = useDispatch();
  const params = useParams();
  const { loggedInUser } = usePermissions();

  const [lastAction, setLastAction] = useState(null);
  const [historyEmpty, setHistoryEmpty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);

  const employeeId = params?.id;
  const customerId = loggedInUser?.customerId;

  const getEmployeeDetails = useSelector((state) => state.GetEmployeeDetails.getEmployeeDetails);
  const clockinEnabled = getEmployeeDetails?.payload?.clockinTrackingEnabled || false;

  useEffect(() => {
    if (customerId && employeeId) {
      fetchHistory();
    }
  }, [customerId, employeeId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const historyResponse = await getClockinHistory(customerId, employeeId, { page: 1, limit: 10 });
      let history = [];

      if (historyResponse?.success && historyResponse?.data) {
        if (Array.isArray(historyResponse.data)) {
          history = historyResponse.data;
        } else if (historyResponse.data.rows) {
          history = historyResponse.data.rows;
        }
      } else if (historyResponse?.rows) {
        history = historyResponse.rows;
      } else if (Array.isArray(historyResponse?.data)) {
        history = historyResponse.data;
      }

      setHistoryEmpty(history.length === 0);

      if (history.length > 0) {
        const latestEntry = history[0];
        setLastAction({ action: latestEntry.action, actionAt: latestEntry.actionAt });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching clock-in history:", error);
      setHistoryEmpty(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEnableClockin = async (empId) => {
    try {
      setEnabling(true);
      const response = await updateClockinSettings(customerId, {
        action: "enable",
        employeeIds: [empId],
      });

      if (response?.success || response) {
        dispatch(
          showSnackbar({
            message: "Clock in/out feature enabled successfully",
            type: "success",
            dispatch,
          })
        );
        dispatch(GetEmployeeDetails({ request: `customer/${customerId}/employee/${empId}` }));
        // Refresh history so the not-activated screen goes away
        fetchHistory();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error enabling clock-in:", error);
      dispatch(
        showSnackbar({
          message: error?.message || "Failed to enable clock in/out feature",
          type: "error",
          dispatch,
        })
      );
    } finally {
      setEnabling(false);
    }
  };

  if (loading || enabling) {
    return <PageLoader />;
  }

  // Show not-activated screen only when clock-in is disabled AND no history exists
  const showNotActivated = !clockinEnabled && historyEmpty;

  return (
    <Box>
      {showNotActivated ? (
        <TimesheetNotActivated employeeId={employeeId} onEnable={handleEnableClockin} />
      ) : (
        <CustomerTimesheetView customerId={customerId} employeeId={employeeId} lastAction={lastAction} />
      )}
    </Box>
  );
}

export default TimesheetTab;
